import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { sessionListSearchValueAtom } from '@/atoms/sideNavAtoms';
import { DatadogDurationVital } from '@/components/constants/datadog-vitals';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { SESSION_SEARCH_DEBOUNCE_MS } from '@/components/layout/Navigation/sessions/constants';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { datadogRum } from '@datadog/browser-rum';
import { Button } from '@heidi/design-system/button';
import { SearchInput } from '@heidi/design-system/search-input';
import { useTranslations } from '@heidi/runtime/i18n';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { X } from 'lucide-react';
import { m, type Variants } from 'motion/react';
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import { isSessionSearchBarOpenAtom } from './search-bar.atoms';

function SessionBarSearchBar() {
  const { t } = useTranslations();
  const intl = useIntl();
  const clearSearchLabel = intl.formatMessage({
    defaultMessage: 'Clear search',
    id: '4YJHutR26p',
  });
  const isMobile = useIsMobile();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const searchValue = useAtomValue(sessionListSearchValueAtom);
  const setSearchValue = useSetAtom(sessionListSearchValueAtom);
  const [inputValue, setInputValue] = useState(() =>
    isSidebarPanelEnabled ? searchValue : ''
  );
  const [sessionQueryVersion, setSessionQueryVersion] = useAtom(sessionQueryVersionAtom);
  const [isSessionSearchBarOpen, setIsSessionSearchBarOpen] = useAtom(
    isSessionSearchBarOpenAtom
  );

  useEffect(() => {
    if (isMobile) {
      setIsSessionSearchBarOpen(true);
    } else {
      if (isSidebarPanelEnabled) return;
      setIsSessionSearchBarOpen(false);
    }
  }, [isMobile, isSidebarPanelEnabled, setIsSessionSearchBarOpen]);

  const variants: Variants = useMemo(
    () => ({
      open: {
        opacity: 1,
        height: isMobile ? '40px' : '30px',
        transition: {
          duration: 0.2,
          ease: 'easeInOut',
        },
      },
      closed: {
        opacity: 0,
        height: 0,
        transition: {
          duration: 0.2,
          ease: 'easeInOut',
        },
      },
    }),
    [isMobile]
  );

  const handleBlur = useCallback(() => {
    if (!isMobile && isEmpty(inputValue)) {
      if (isSidebarPanelEnabled && !isEmpty(searchValue)) return;
      setIsSessionSearchBarOpen(false);
    }
  }, [
    inputValue,
    isMobile,
    isSidebarPanelEnabled,
    searchValue,
    setIsSessionSearchBarOpen,
  ]);

  // Debounced update to searchValue
  const debouncedSetSearchValue = useDebouncedCallback((value: string) => {
    // Was having issues using useAtomValue so using getDefaultStore() directly here
    const sessionQueryVersion = getDefaultStore().get(sessionQueryVersionAtom);
    const sessionQueryVersionToDatadogDurationVital = {
      v1: DatadogDurationVital.SESSION_SEARCH_RENDERED_V1,
      v2: DatadogDurationVital.SESSION_SEARCH_RENDERED_V2,
      v3: DatadogDurationVital.SESSION_SEARCH_RENDERED_V3,
    };
    datadogRum.startDurationVital(
      sessionQueryVersionToDatadogDurationVital[sessionQueryVersion]
    );
    setSearchValue(value);
  }, SESSION_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    // Keep the local input UI in sync when search is cleared externally
    // (e.g. "Clear filters" resets the shared search atom).
    // Also cancel any pending debounced write so stale text is not restored.
    if (isEmpty(searchValue)) {
      debouncedSetSearchValue.cancel();
      setInputValue('');
    }
  }, [debouncedSetSearchValue, searchValue]);

  const handleClear = useCallback(() => {
    debouncedSetSearchValue.cancel();
    setSearchValue('');
    setInputValue('');
  }, [debouncedSetSearchValue, setSearchValue]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      debouncedSetSearchValue(e.target.value);
    },
    [debouncedSetSearchValue]
  );

  const shouldShowSearchBar =
    isSessionSearchBarOpen || (isSidebarPanelEnabled && !isEmpty(searchValue));

  const searchInput = (
    <SearchInput
      density={isMobile ? 'comfortable' : 'compact'}
      aria-label="Session search input"
      data-testid="session-list-search-bar"
      placeholder={t('shared.Search')}
      disabled={isOfflineGated}
      wrapperClassName="bg-transparent transition-all"
      onClick={() => {
        if (sessionQueryVersion === 'v2') {
          setSessionQueryVersion('v1');
        }
      }}
      onBlur={handleBlur}
      onClear={handleClear}
      clearAriaLabel={clearSearchLabel}
      accessoryEnd={
        isOfflineGated && inputValue ? (
          // SearchInput hides its built-in clear while disabled, but clearing is
          // client-side (Fuse over the cached list) and must stay live offline.
          <Button
            iconOnly
            variant="ghost"
            size="xxs"
            onClick={handleClear}
            className="text-text-secondary"
            aria-label={clearSearchLabel}
          >
            <X size={isMobile ? 20 : 14} aria-hidden="true" />
          </Button>
        ) : undefined
      }
      value={inputValue}
      onChange={onChange}
    />
  );

  return (
    <m.div
      initial="closed"
      animate={shouldShowSearchBar ? 'open' : 'closed'}
      variants={variants}
      className="overflow-hidden"
    >
      {/* Offline: the input is disabled per the offline read-only policy (search
          itself is client-side Fuse over the cached list); wrap it so hovering
          the greyed field explains why. The clear affordance stays live —
          clearing is client-side. */}
      {isOfflineGated ? (
        <OfflineGatedTooltip isOfflineGated className="w-full">
          {searchInput}
        </OfflineGatedTooltip>
      ) : (
        searchInput
      )}
    </m.div>
  );
}

SessionBarSearchBar.displayName = 'SessionBarSearchBar';
export default SessionBarSearchBar;
